import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useUserContext } from '../../../contexts/UserContext';
import { PrimaryButton, SecondaryButton } from "../../UI";
import PrimaryModal from '../../UI/Modal/PrimaryModal';
import Select from '../../UI/Select';
import Input, { Textarea } from '../../UI/Input';
import Toggle from '../../UI/Toggle';
import { createEpmChecklist, updateEpmChecklist } from '../../../utils/APIs';
import { EPM_LIMITS } from '../validation/epmRules';
import { oneOffChecklistSchema } from '../validation/epmSchemas';
import EpmCharCounter from '../EpmCharCounter';

const ChecklistModal = ({ checklist = null, defaultEnv = 'STAGING', onClose, onSaved, lockEnv = false, testId = null }) => {
    const { userData } = useUserContext();
    const token = userData?.token;
    const orgToken = userData?.selectedOrganization?.org_token;

    const isEdit = Boolean(checklist?._id);

    const initialValues = useMemo(() => ({
        env: checklist?.env || defaultEnv || 'STAGING',
        name: checklist?.name || '',
        description: checklist?.description || '',
        isRequired: checklist?.isRequired !== undefined ? checklist.isRequired : true,
    }), [checklist, defaultEnv]);

    const [form, setForm] = useState(initialValues);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm(initialValues);
        setErrors({});
    }, [initialValues]);

    const setValue = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const validate = () => {
        const nextErrors = {};
        try {
            oneOffChecklistSchema.validateSync(form, { abortEarly: false });
        } catch (err) {
            if (err.inner && err.inner.length > 0) {
                err.inner.forEach((validationErr) => {
                    nextErrors[validationErr.path] = validationErr.message;
                });
            } else {
                nextErrors[err.path || 'form'] = err.message;
            }
        }
        if (!form.env) nextErrors.env = 'Environment is required.';
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = {
                env: form.env,
                name: form.name.trim(),
                description: form.description.trim(),
                isRequired: Boolean(form.isRequired),
                ...(testId && { test_id: testId }),
            };

            let savedItem = null;
            if (isEdit) {
                const res = await updateEpmChecklist(token, orgToken, checklist._id, payload);
                savedItem = res?.data || res;
                toast.success('Checklist item updated successfully.');
            } else {
                const res = await createEpmChecklist(token, orgToken, payload);
                savedItem = res?.data || res;
                toast.success('Checklist item created successfully.');
            }

            if (onSaved) onSaved(savedItem);
        } catch (error) {
            const message = error?.message || `Unable to ${isEdit ? 'update' : 'create'} the checklist item.`;
            toast.error(message);
            setErrors((prev) => ({ ...prev, form: message }));
        } finally {
            setSaving(false);
        }
    };

    const modalTitle = isEdit
        ? 'Edit Checklist Item'
        : testId
            ? 'Add One-off Check'
            : 'Add New Checklist Item';

    return (
        <PrimaryModal title={modalTitle} hideModal={onClose} loading={saving} widthClass="max-w-lg">
            <form className="epm-location-modal" onSubmit={handleSubmit}>
                <div className="epm-location-modal__grid">
                    {/* Environment selection */}
                    <label className="epm-location-modal__full">
                        <span>Environment</span>
                        <Select
                            id="env"
                            name="env"
                            value={form.env}
                            onChange={(e) => setValue('env', e.target.value)}
                            disabled={isEdit || lockEnv}
                            options={[
                                { value: 'STAGING', label: 'Staging' },
                                { value: 'PRODUCTION', label: 'Production' },
                            ]}
                        />
                        {errors.env && <small>{errors.env}</small>}
                    </label>

                    {/* Name */}
                    <div className="epm-location-modal__full">
                        <Input
                            id="name"
                            name="name"
                            label="Name"
                            autoFocus
                            maxLength={EPM_LIMITS.ONE_OFF_CHECKLIST_NAME_MAX}
                            showCharCount
                            value={form.name}
                            onChange={(e) => setValue('name', e.target.value)}
                            placeholder="e.g. Renders on iOS Safari and Android Chrome"
                            error={errors.name}
                        />
                    </div>

                    {/* Description */}
                    <div className="epm-location-modal__full">
                        <Textarea
                            id="description"
                            name="description"
                            label="Description"
                            rows={3}
                            maxLength={EPM_LIMITS.ONE_OFF_CHECKLIST_DESC_MAX}
                            showCharCount
                            value={form.description}
                            onChange={(e) => setValue('description', e.target.value)}
                            placeholder="Details or verification instructions for this check…"
                            error={errors.description}
                        />
                    </div>

                    {/* isRequired Toggle */}
                    <div className="epm-location-modal__full flex items-center gap-3 pt-1">
                        <Toggle
                            id="isRequired"
                            checked={form.isRequired}
                            onChange={(e) => setValue('isRequired', e.target.checked)}
                            size="sm"
                        />
                        <label htmlFor="isRequired" className="text-xs font-medium text-stride-heading cursor-pointer select-none">
                            Required check (REQ) — gates test progression
                        </label>
                    </div>
                </div>

                {errors.form && (
                    <p className="epm-location-modal__form-error" role="alert">{errors.form}</p>
                )}

                <div className="flex justify-end gap-2.5 mt-6">
                    <SecondaryButton type="button" size="sm" disable={saving} handleClick={onClose}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton type="submit" size="sm" loading={saving}>
                        {isEdit ? 'Save Changes' : 'Add Item'}
                    </PrimaryButton>
                </div>
            </form>
        </PrimaryModal>
    );
};

export default ChecklistModal;
